<script setup lang="ts">
import { computed } from "vue"
import { useComparatorStore } from "@modules/courtage/stores/comparator.store"
import { storeToRefs } from "pinia"
import { useI18n } from "vue-i18n"

const { t } = useI18n({
    messages: {
        fr: {
            non_teleoperable:
                "Ce compteur n’est pas téléopérable : la comparaison d’offres et l’analyse sur la consommation (courbe de charge, données journalières) ne sont pas possibles. Vous pouvez consulter les données techniques, contractuelles et les agrégats de facturation le cas échéant.",
            title: "Choix du compteur et disponibilité des données",
            radio_title:
                "Sélectionnez d'abord un compteur pour lequel vous souhaitez comparer les offres afin de <strong>vérifier la complétude des données nécessaires pour la comparaison.</strong>",

            message:
                "Veuillez <strong>vérifier les données contractuelles et techniques</strong>, et <strong>indiquer la période de référence</strong> de la comparaison ci-dessous",
            tabs: {
                info: "Données contractuelles et techniques",
                offer: "Tarification et fournisseur",
                period: {
                    label: "Période de référence",
                    no_best_period:
                        "Aucune période idéale de 1 an sans trou de donnée a été détectée. Veuillez sélectionner une période personnalisée.",
                    tooltip:
                        "La période de référence correspond à la période pendant laquelle votre consommation d’électricité est prise en compte pour comparer les offres. Elle s’étend de la date de début à la date de fin de votre relevé.",
                },
            },
            button: { newMeter: "Nouveau compteur" },
        },
    },
})

const comparatorStore = useComparatorStore()

const { getSourceById, hasErrorPeriods, bestPeriodAvailable } =
    storeToRefs(comparatorStore)

/**
 * PDL non téléopérable : pas d’éligibilité à la comparaison basée sur la consommation.
 */
const getIsSelectedPdlNonTeleoperable = computed(
    () =>
        getSourceById.value?.type === "pdl" &&
        getSourceById.value?.informations?.pdl?.compteur?.teleoperable === false
)
</script>

<template>
    <div
        class="comparator-meter-choice-card card p-6 flex flex-col gap-6 bg-white rounded-md"
    >
        <h2 class="text-lg font-bold">{{ t("title") }}</h2>
        <div class="flex flex-col gap-8 card p-4 rounded-md">
            <p v-html="t('radio_title')" class="text-sm" />

            <!-- ================================ -->
            <!-- LISTE DES COMPTEURS              -->
            <!-- ================================ -->
            <ComparatorMeterChoiceRadioList />

            <BaseMessage
                v-if="getSourceById && getIsSelectedPdlNonTeleoperable"
                severity="error"
                class="w-full"
            >
                {{ t("non_teleoperable") }}
            </BaseMessage>

            <div
                class="flex flex-col gap-3"
                v-else-if="getSourceById?.eligible"
            >
                <p
                    class="text-sm text-surface-800 p-4"
                    v-html="t('message')"
                ></p>

                <!-- ================================ -->
                <!-- ONGLETS                          -->
                <!-- ================================ -->
                <div class="card pb-4 px-4">
                    <BaseTabs value="info">
                        <BaseTabList
                            :pt="{
                                tabList: 'flex flex-row',
                            }"
                        >
                            <BaseTab
                                :pt="{
                                    root: 'flex-1',
                                }"
                                value="info"
                                >{{ t("tabs.info") }}</BaseTab
                            >
                            <BaseTab
                                :pt="{
                                    root: 'flex-1',
                                }"
                                value="offer"
                                >{{ t("tabs.offer") }}</BaseTab
                            >
                            <BaseTab
                                :pt="{
                                    root: 'flex-1',
                                }"
                                value="period"
                                ><div
                                    class="flex items-center gap-2 justify-center"
                                >
                                    {{ t("tabs.period.label") }}
                                    <i
                                        class="pi pi-info-circle"
                                        v-tooltip="t('tabs.period.tooltip')"
                                        style="font-size: 0.8rem"
                                    ></i>
                                    <i
                                        v-if="hasErrorPeriods"
                                        class="pi pi-exclamation-triangle text-red-500 ml-1"
                                        v-tooltip="
                                            hasErrorPeriods &&
                                            !bestPeriodAvailable
                                                ? t(
                                                      'tabs.period.no_best_period'
                                                  )
                                                : ''
                                        "
                                    ></i>
                                </div>
                            </BaseTab>
                        </BaseTabList>
                        <BaseTabPanels :pt="{ root: 'px-0 pb-0 pt-4' }">
                            <BaseTabPanel value="info">
                                <!-- ================================ -->
                                <!-- DONNÉES CONTRACTUELLES ET TECHNIQUES -->
                                <!-- ================================ -->
                                <ComparatorMeterInfoData />
                            </BaseTabPanel>
                            <BaseTabPanel value="offer">
                                <!-- ================================ -->
                                <!-- TARIFICATION ET FOURNISSEUR -->
                                <!-- ================================ -->
                                <ComparatorMeterInfoOffer />
                            </BaseTabPanel>
                            <BaseTabPanel value="period">
                                <!-- ================================ -->
                                <!-- PÉRIODE DE RÉFÉRENCE -->
                                <!-- ================================ -->
                                <ComparatorMeterInfoPeriod />
                            </BaseTabPanel>
                        </BaseTabPanels>
                    </BaseTabs>
                </div>
            </div>

            <!-- ================================ -->
            <!-- Bouton ajouter nouveau compteur  -->
            <!-- ================================ -->
            <BaseButton
                icon="pi pi-plus"
                variant="link"
                class="w-fit"
                :label="t('button.newMeter')"
            />
        </div>
    </div>
</template>
